import { useMemo } from 'react';
import getUserFromMeetingInvite from '../../../../misc/getUserFromMeetingInvite';
import { useI18n } from '../../i18nHooks';
import { useMyUserId } from '../../sessionHooks';
import { useMeeting } from '../useMeeting';

/**
 * Returns an array of invites for a given meeting, optionally including the meeting initiator and excluding the current user.
 *
 * @param {number} eventId - The ID of the event.
 * @param {number} meetingId - The ID of the meeting.
 * @param {Object} options - An optional object containing includeInitiator and excludeSelf flags.
 * @param {boolean} options.includeInitiator - Whether to include the meeting initiator in the returned array. Defaults to false.
 * @param {boolean} options.excludeSelf - Whether to exclude the current user from the returned array. Defaults to true.
 * @return {Array} - An array of meeting invites.
 */
export function useMeetingInvites(eventId, meetingId, { includeInitiator = false, excludeSelf = true } = {}) {
    const meeting = useMeeting(eventId, meetingId),
        selfUserId = useMyUserId(),
        i18n = useI18n();

    return useMemo(() => {
        if (!meeting) return [];

        let result = meeting.invited.map((invite) => ({
            ...invite,
            user: getUserFromMeetingInvite(invite, i18n),
        }));

        if (includeInitiator) {
            result.unshift({
                user: meeting.initiator,
                state: meeting.state,
            });
        }

        if (excludeSelf) {
            result = result.filter((invite) => invite.user.id != selfUserId);
        }

        return result;
    }, [meeting, includeInitiator]);
}
